import { StyleSheet, css } from 'aphrodite'
import type React from 'react'
import { useEffect, useState } from 'react'
import { useUser } from '../../../contexts/UserContext'
import { userService } from '../../../services/userService'
import OnboardingHeader from '../OnboardingHeader'

interface OnboardingStepCompleteProps {
  onClose: () => void
}

const OnboardingStepComplete: React.FC<OnboardingStepCompleteProps> = ({
  onClose,
}) => {
  const { user, refreshUser } = useUser()
  const [error, setError] = useState<string | null>(null)
  const [hasAttempted, setHasAttempted] = useState(false)

  useEffect(() => {
    const completeOnboarding = async () => {
      // Don't run if no user, already completed, or already attempted (including on error)
      if (!user || user.has_completed_driver_onboarding || hasAttempted) return

      setHasAttempted(true)

      try {
        await userService.completeDriverOnboarding(user.id)
        // Refresh user context to get updated has_completed_driver_onboarding flag
        await refreshUser()
      } catch (err) {
        console.error('Failed to complete driver onboarding:', err)
        setError(
          err instanceof Error ? err.message : 'Failed to complete onboarding',
        )
      }
    }

    completeOnboarding()
  }, [user, refreshUser, hasAttempted])

  return (
    <div className={css(styles.root)}>
      <div className={css(styles.content)}>
        <OnboardingHeader title="Review and Complete" progress={100} />
        <div className={css(styles.successIcon)}>✓</div>
        <h3 className={css(styles.title)}>You're all set!</h3>
        <p className={css(styles.subtitle)}>
          Your profile has been successfully updated. You can now start using
          the ecosystem.
        </p>
        {error && <div className={css(styles.error)}>{error}</div>}
      </div>
      <div className={css(styles.buttonRow)}>
        <button
          className={css(styles.primaryButton)}
          onClick={onClose}
          disabled={!!error}
        >
          Go to Dashboard
        </button>
      </div>
    </div>
  )
}

const styles = StyleSheet.create({
  root: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    padding: 'clamp(24px, 5vw, 48px)',
  },
  successIcon: {
    fontSize: 80,
    color: '#22C55E',
    marginBottom: 32,
  },
  title: {
    fontSize: 'clamp(24px, 4vw, 32px)',
    fontWeight: 600,
    color: '#1F3A8A',
    margin: 'clamp(12px, 2vw, 20px) 0 clamp(6px, 1vw, 12px) 0',
  },
  subtitle: {
    fontSize: 'clamp(14px, 2vw, 18px)',
    color: '#222',
    marginBottom: 'clamp(24px, 4vw, 40px)',
    maxWidth: 400,
  },
  primaryButton: {
    background: '#2563EB',
    color: '#fff',
    border: 'none',
    borderRadius: 12,
    padding: 'clamp(12px, 2vw, 16px) clamp(24px, 4vw, 32px)',
    fontSize: 'clamp(14px, 2vw, 18px)',
    fontWeight: 600,
    cursor: 'pointer',
    ':disabled': {
      opacity: 0.7,
      cursor: 'not-allowed',
    },
  },
  error: {
    color: '#DC2626',
    fontSize: 14,
    marginBottom: 16,
  },
  buttonRow: {
    marginTop: 'clamp(24px, 4vw, 40px)',
  },
})

export default OnboardingStepComplete
